import type {ReviewSummary} from '../../../../../page-data/payloads/review-summaries'
import type {Thread} from '@github-ui/diff-lines/conversation-types'

export interface ReviewThread {
  thread: Thread
  relevantCommentIds: string[]
}

export interface ReviewWithComments {
  summary: ReviewSummary
  threads: ReviewThread[]
}

export function assignThreadsToReviews(
  reviewSummaries: ReviewSummary[],
  threads: Thread[],
): {reviews: ReviewWithComments[]; unassignedThreads: Thread[]} {
  /** Map of review ID -> deduped review summary. Summaries appear once for each review they contain. */
  const summariesById = new Map(reviewSummaries.map(summary => [summary.id, summary]))

  /** Map of review -> thread -> review-relevant comment IDs */
  const threadsByReview = new Map<ReviewSummary, Map<Thread, Set<string>>>()
  const assignedThreadIds = new Set<string>()

  // Comments belong to reviews, not threads. But comments also belong to threads, so threads indirectly are tied
  // to reviews. And (for now) only comments know which review they belong to - threads don't have data on comments.
  // Also, a single review may post more than one comment in a single thread. So it's quite complicated to get
  // backwards from the comments back to the reviews.
  for (const thread of threads) {
    const comments = thread.commentsData?.comments ?? []

    for (const comment of comments)
      if (comment.reviewId) {
        const review = summariesById.get(String(comment.reviewId))
        if (!review) continue

        const reviewThreads = threadsByReview.get(review) ?? new Map<Thread, Set<string>>()
        const relevantComments = reviewThreads.get(thread) ?? new Set<string>()

        relevantComments.add(comment.id)
        assignedThreadIds.add(thread.id)
        reviewThreads.set(thread, relevantComments)
        threadsByReview.set(review, reviewThreads)
      }
  }

  const reviewsWithThreads = reviewSummaries.map(summary => ({
    summary,
    threads: Array.from(threadsByReview.get(summary) ?? []).map(([thread, relevantCommentIds]) => ({
      thread,
      relevantCommentIds: Array.from(relevantCommentIds),
    })),
  }))

  return {
    reviews: reviewsWithThreads,
    unassignedThreads: threads.filter(thread => !assignedThreadIds.has(thread.id)),
  }
}
